import React, { FC, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useWatch } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import {
  CircleCheckIcon,
  CloudUploadIcon,
  DownloadIcon,
  ImportIcon,
  InfoCircle,
  UploadIcon,
} from '../../../../../assets/icons/index';
import LoaderIcon from '../../../../../assets/icons/loader-icon';
import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import Button from '../../../../../components/button/index';
import { CloseModalButton } from '../../../../../components/close-modal-button/index';
import { Sheet, SheetContent } from '../../../../../components/sheet/index';
import { BackendRoute } from '../../../../../config/index';
import { QueryKey } from '../../../../../constants/index';
import { useAppMutation, useLocale, useReactForm } from '../../../../../hooks/index';
import { ApiService } from '../../../../../services/index';
import { PageRoute } from '../../../../../types/page.types';
import { StakeholderTypeGroup } from '../../../../../types/stakeholder.types';
import { ModifyGuideTab } from '../../../../modify-guide/modify-guide.types';
import StakeholderTypeGroupCombobox from '../stakeholder-type-combobox';
import { FormSchema, formSchema } from './bulk-import.validation';

export type BulkImportStakeholdersModalProps = {
  companyId: string;
  invalidateQuery: () => void;
  isOpenModal: boolean;
  setOpen: (state: boolean) => void;
};

const BulkImportStakeholdersModal: FC<BulkImportStakeholdersModalProps> = ({
  companyId,
  invalidateQuery,
  isOpenModal,
  setOpen,
}) => {
  const { locale, messagesLocale } = useLocale();
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    getValues,
    getFieldState,
    formState: { isValid, errors, dirtyFields },
  } = useReactForm({ schema: formSchema(messagesLocale) });

  const { mutate: mutateTemplate } = useAppMutation(
    [QueryKey.STAKEHOLDERS_DOWNLOAD_TEMPLATE, companyId],
    {
      mutationFn: (typeGroup: StakeholderTypeGroup) =>
        ApiService.downloadFile(
          {
            endpoint: BackendRoute.COMPANIES,
            routePath: [companyId, 'stakeholders', typeGroup, 'template'],
          },
          { locale },
        ),
    },
  );

  const { mutate, isPending } = useAppMutation([QueryKey.STAKEHOLDERS_BULK_IMPORT, companyId], {
    mutationFn: async ({ file, typeGroup }: { file: File; typeGroup: StakeholderTypeGroup }) => {
      const fileData = new FormData();
      fileData.append('file', file);
      return ApiService.requestWithAuthResponse(
        `${BackendRoute.COMPANIES}/${companyId}/stakeholders/${typeGroup}/import`,
        {
          method: 'POST',
          body: fileData,
          locale,
        },
      );
    },
    onSuccess: () => {
      setOpen(false);
      reset();
      invalidateQuery();
      toast.success(messagesLocale['STAKEHOLDERS_SUCCESSFULLY_IMPORTED']);
    },
  });
  const handleCloseModal = useCallback(() => {
    reset();
    setOpen(false);
  }, [reset, setOpen]);

  const submitHandler = useCallback(
    (data: FormSchema) => {
      mutate({ file: data.file, typeGroup: data.stakeholderTypeGroup });
    },
    [mutate],
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setValue('file', acceptedFiles[0], { shouldValidate: true });
    },
    [setValue],
  );

  const stakeholderType = useWatch({ control, name: 'stakeholderTypeGroup' });

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    noClick: false,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    disabled: !stakeholderType,
  });

  const isValidFileState = getValues('file') && !getFieldState('file').invalid;
  return (
    <Sheet onOpenChange={(open) => !open && handleCloseModal()} open={isOpenModal}>
      <SheetContent
        className="w-full max-w-[485px] border-transparent bg-transparent p-2 shadow-none"
        onInteractOutside={handleCloseModal}
        side="RIGHT"
      >
        <div className="flex h-full w-full flex-col overflow-hidden overflow-y-auto rounded-lg border-[1px] border-gray-300 bg-gray-100 pb-4">
          <div className="flex h-fit w-full items-center justify-between bg-white px-6 py-3">
            <span className="text-xl font-[550] text-gray-700">
              <AppFormattedMessage id="IMPORT_STAKEHOLDERS" />
            </span>
            <CloseModalButton onClose={handleCloseModal} />
          </div>
          <form className="flex h-full flex-col gap-4" onSubmit={handleSubmit(submitHandler)}>
            <div className="flex h-full flex-col gap-6 overflow-y-auto rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
              <StakeholderTypeGroupCombobox control={control} name="stakeholderTypeGroup" />

              <div className="flex h-11 w-full justify-between">
                <Button
                  className={twMerge(
                    'flex w-fit items-center gap-4 rounded border border-gray-100 px-3 py-[6px] text-sm font-[450] text-gray-700 shadow-xs',
                  )}
                  disabled={!dirtyFields.stakeholderTypeGroup}
                  onClick={() => mutateTemplate(getValues('stakeholderTypeGroup'))}
                  styleType="NONE"
                  type="button"
                >
                  <DownloadIcon
                    iconColor={!dirtyFields.stakeholderTypeGroup ? '#D0D5DD' : '#344054'}
                  />
                  <AppFormattedMessage id="DOWNLOAD_TEMPLATE" />
                </Button>

                <Link
                  className="flex w-fit cursor-pointer items-center justify-center gap-2 rounded text-sm font-[450] text-brand-700 underline disabled:cursor-default disabled:bg-gray-100 disabled:text-gray-300 disabled:ring-1 disabled:ring-gray-200"
                  rel="noopener noreferrer"
                  target="_blank"
                  to={`${PageRoute.GUIDES}/${ModifyGuideTab.IMPORT_STAKEHOLDERS}`}
                >
                  <AppFormattedMessage id="VIEW_IMPORT_GUID" />
                  <ImportIcon />
                </Link>
              </div>
              <input {...getInputProps()} accept=".xlsx" className="hidden" type="file" />
              <label
                {...getRootProps()}
                className={twMerge(
                  'relative flex w-full border-spacing-[7px] flex-col items-center gap-6 rounded-lg border border-dashed border-gray-200 py-6',
                  stakeholderType ? 'cursor-pointer' : 'cursor-not-allowed',
                )}
              >
                {isValidFileState ? (
                  isPending ? (
                    <LoaderIcon className="size-14" />
                  ) : (
                    <CircleCheckIcon />
                  )
                ) : (
                  <>
                    {errors.file ? (
                      <InfoCircle className="size-14" thinIconVariant />
                    ) : (
                      <CloudUploadIcon />
                    )}
                  </>
                )}
                <div className="flex flex-col items-center gap-2 text-center">
                  <div className="flex justify-center">
                    {isValidFileState ? (
                      <span className="text-sm font-[550] text-forest-500">
                        <AppFormattedMessage id="FILE_PROCESSED_SUCCESSFULLY" />
                      </span>
                    ) : errors.file ? (
                      <span className="text-sm font-[550] text-fireside-600">
                        {errors.file.message?.toString()}
                      </span>
                    ) : (
                      <p className="text-sm font-medium text-gray-700">
                        <span className="underline">
                          <AppFormattedMessage id="CLICK_TO_UPLOAD" />{' '}
                        </span>
                        <AppFormattedMessage id="OR_DRAG_AND_DROP" />
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-1 text-[10px] font-[450] leading-4 text-gray-500">
                    {isValidFileState ? (
                      <>
                        <span>{(getValues('file') as unknown as File).name}</span>
                        <span>
                          {((getValues('file') as unknown as File).size / 1024).toFixed(3)}
                          <AppFormattedMessage id="KILOBYTE" />
                        </span>
                      </>
                    ) : (
                      <>
                        <span>
                          <AppFormattedMessage
                            id="SUPPORTED_FORMATS"
                            values={{
                              formats: 'xlsx',
                            }}
                          />
                        </span>
                        <span>
                          <AppFormattedMessage id="MAXIMUM_FILE_SIZE" values={{ size: 10 }} />
                        </span>
                      </>
                    )}
                  </div>
                  {errors.file && (
                    <span className="flex h-9 w-fit items-center gap-1 rounded border border-gray-100 bg-gray-25 px-3 py-[6px] text-sm font-[450] text-gray-700">
                      <UploadIcon />
                      <AppFormattedMessage id="UPLOAD_AGAIN" />
                    </span>
                  )}
                </div>
              </label>
              {isValidFileState && !getValues('stakeholderTypeGroup') && (
                <span className="flex items-start gap-1 text-xs font-[450] text-fireside-600">
                  <InfoCircle className="mt-[2px]" />
                  Please select Stakeholder Type from the dropdown before saving upload
                </span>
              )}
            </div>

            <div className="flex h-9 w-full justify-end gap-3 px-6">
              <Button
                className="h-full w-fit rounded border border-gray-100 bg-white px-3 py-[6px] text-sm font-[450] text-gray-700"
                onClick={() => setOpen(false)}
                styleType="NONE"
                type="button"
              >
                <AppFormattedMessage id="CLOSE" />
              </Button>
              <Button
                className="h-full w-fit px-4 py-[6px] text-sm font-[550] text-white"
                disabled={!isValid || isPending}
                isLoading={isPending}
                type="submit"
              >
                <AppFormattedMessage id="SAVE" />
              </Button>
            </div>
          </form>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default BulkImportStakeholdersModal;
